import * as React from "react";
import {observer} from "mobx-react";
import {makeObservable, observable, runInAction} from "mobx";
import {withAppStores} from "../../../AppStores";
import {XyiconFeature} from "../../../generated/api/base";
import type {IModel} from "../../../data/models/Model";
import {getModelId} from "../../../data/models/Model";
import type {View} from "../../../data/models/View";
import type {AppState} from "../../../data/state/AppState";
import type {Pointer} from "../../../utils/interaction/Pointer";
import {Signal} from "../../../utils/signal/Signal";
import type {IFieldPointer} from "../../../data/models/field/Field";
import type {IViewColumn} from "../../../data/models/ViewUtils";
import {getSortForField} from "../../../data/models/ViewUtils";
import {toggleTableSort} from "../../widgets/table/TableUtils";
import {InitialsV5, IsRoundClassName} from "../widgets/InitialsV5";
import type {ManageColumnParam} from "../../widgets/table/TableHeaderDropDown";
import {ArrayUtils} from "../../../utils/data/array/ArrayUtils";
import {EmptyListViewV5} from "../details/EmptyListViewV5";
import type {ScreenType} from "../../modules/abstract/ScreenType";
import type {IViewTab} from "../topbar/ViewTabsCommon";
import {getLocalStorageKeyForWorkspaceViews} from "../topbar/ViewTabsCommon";
import type {Report} from "../../../data/models/Report";
import type {SpotlightColorMode, TableRowV5} from "./table/TableRowV5";
import {TableV5} from "./table/TableV5";
import {GridActionBarV5} from "./GridActionBarV5";
import type {IActionBarItem} from "./ModuleViewV5";
import {NoResultSearchViewV5} from "./table/NoResultSearchViewV5";
import {NoSearchResultsWrapper} from "./table/NoResultStyled";

interface IGridViewProps<T extends IModel> {
	readonly feature: XyiconFeature;
	readonly items: T[];
	readonly selected: T[];
	readonly focused: T[];
	readonly view: View;
	readonly onFocus: (items: T[], isGoToItemNeeded?: boolean) => void;
	readonly onSelect: (items: T[]) => void;
	readonly onClick: (item: T) => void;
	readonly onDoubleClick: (item: T) => void;
	readonly onAddClick: () => void;
	readonly onTableReady?: (table: TableV5<T>) => void;
	readonly appState?: AppState;
	readonly checkboxColumn?: boolean;
	readonly searchString: string;
	readonly isSearching: boolean;
	readonly tableRowIcon?: boolean;
	readonly isCellContentsWrappingOn?: boolean;
	readonly prepareFieldsInRow?: boolean;
	readonly emptyListText?: React.ReactNode;
	readonly onManageColumns?: () => void;
	readonly actionBarButtons?: IActionBarItem<T>[];
	readonly actionBarDefaultClick: (item: IActionBarItem<T>) => void;
	readonly onCloseGridActionBar?: () => void;
	readonly onDuplicateClick?: (item?: T) => Promise<void>;
	readonly selectedScreen?: ScreenType;
	readonly getItemColor?: (item: T) => string | null;
	readonly colorApplicationMode?: SpotlightColorMode;
	readonly getSpotlightFieldRefID?: (item: T) => string | null;
	readonly getColorApplicationMode?: (item: T) => SpotlightColorMode;
	readonly getSpotlightFieldColor?: (item: T) => string | null;
	readonly runReport?: (reports: Report[]) => Promise<void>;
}

@withAppStores("appState")
@observer
export class GridViewV5<T extends IModel> extends React.Component<IGridViewProps<T>> {
	private _appState: AppState;

	private _feature: XyiconFeature;

	private _view: View;

	constructor(props: IGridViewProps<T>) {
		super(props);
		makeObservable<this, "_appState" | "_feature" | "_view">(this, {
			_appState: observable,
			_feature: observable,
			_view: observable,
		});
		this._appState = props.appState;
		this._feature = props.feature;
		this._view = props.view;
		this._committedSelectedIds = props.selected.map((item) => item.id);
		this._committedFocusedIds = props.focused.map((item) => item.id);
	}

	private _tableInstance: TableV5<T> | null = null;
	private _tableReadyPending: boolean = false;
	private _legacyTableRef: {current: unknown} | null = null;
	private _committedSelectedIds: string[];
	private _committedFocusedIds: string[];
	private _pendingLocalSelectedIds: string[] | null = null;
	private _pendingLocalFocusedIds: string[] | null = null;
	public static readonly defaultProps: Partial<IGridViewProps<any>> = {
		checkboxColumn: true,
	};

	public signals = {
		onMouseMoveOnRow: Signal.create<Pointer, TableRowV5<T>>(),
	};

	private _lastFilteredData: T[];
	private _lastSelection: T[];
	public _lastItemCount: number;

	private onTableRowMouseMove = (pointer: Pointer, row: TableRowV5<T>) => {
		this.signals.onMouseMoveOnRow.dispatch(pointer, row);
	};

	private onHeaderWidthChange = (index: number, width: number) => {
		this.getView().resizeColumn(index, width, this._feature);
	};

	private onReorderColumn = (index: number, newIndex: number) => {
		this.getView().reorderColumn(index, newIndex, this._feature);
	};

	private onToggleSort = (field: IFieldPointer) => {
		const view = this.getView();

		const sort = getSortForField(view.sorts, field);

		if (field !== `${XyiconFeature.Xyicon}/icon` && field !== `${XyiconFeature.XyiconCatalog}/icon`) {
			view.addSort(toggleTableSort(sort, field));
		}
	};

	private getHeaders = (): IViewColumn[] => {
		const view = this.getView();

		if (view) {
			const columns = view.getValidViewColumns(this._feature);

			return columns.map((column) => ({
				...column,
				title: this._appState.actions.getFieldTitle(column.field),
			}));
		}

		return [];
	};

	private getFields = (item: T, index?: number) => {
		const headers = this.getHeaders();

		return headers.map((column) => {
			return this._appState.actions.renderValue(item, column.field);
		});
	};

	private renderInitialComponent(item: T) {
		const itemType = this._appState.actions.getTypeById(item?.typeId);
		const color = `#${itemType?.settings.color.hex || "FFFFFF"}`;

		return (
			<InitialsV5
				item={item}
				backgroundColor={color}
				name={itemType?.name}
				className={IsRoundClassName}
				size={30}
			/>
		);
	}

	private getFieldsArray = (item: T, index?: number, headers: readonly Partial<IViewColumn>[] = this.getHeaders()) => {
		const {actions} = this._appState;

		return headers.map((column) => {
			if (column.field.includes("icon")) {
				return this.renderInitialComponent(item);
			}
			// single value
			return actions.getValueFromPropagation(item, column.field) ?? actions.renderValue(item, column.field);
		});
	};

	private getSearchedItems = (items: T[]) => {
		const {searchString, isSearching, feature, appState, selected} = this.props;

		if (isSearching) {
			// When searching, don't call searchModels as that takes long
			return [];
		}

		let result: T[];

		if (items.length > 300 && this._lastFilteredData && this.didSelectionChange() && this._lastItemCount === items.length) {
			// Most likely only the selection changed -> use lastFilteredData (it can be very expensive to call searchModels)
			result = this._lastFilteredData;
		} else {
			this._lastFilteredData = appState.actions.searchModelsCached(items, searchString, feature);
			result = this._lastFilteredData;
		}

		this._lastSelection = selected;
		return result;
	};

	private didSelectionChange() {
		if (this._lastSelection === undefined) {
			return true;
		} else {
			return !ArrayUtils.equals(this._lastSelection, this.props.selected);
		}
	}

	private getView(): View {
		return this._view || this._appState.defaultViews[this._feature];
	}

	private onManageColumns = (param: ManageColumnParam) => {
		if (param.type === "manage") {
			this.props.onManageColumns();
		} else if (param.type === "hide") {
			const field = this.getHeaders()[param.index]?.field;

			if (field) {
				const {view, feature} = this.props;

				view?.removeColumnsByRefId?.([field], feature);
			}
		}
	};

	public get table() {
		return this._tableInstance;
	}

	private readonly bindTable = (table: TableV5<T> | null): void => {
		// Legacy callers still use the shared ref. A different grid may have taken
		// ownership since our last commit, so only clear our own binding.
		if (this._legacyTableRef?.current === this._tableInstance) {
			this._legacyTableRef.current = null;
		}
		this._tableInstance = table;
		this._tableReadyPending = table !== null;
		this._legacyTableRef = table ? this.props.appState.tableComponent : null;
		if (this._legacyTableRef) {
			this._legacyTableRef.current = table;
		}
	};

	private notifyTableReady(): void {
		if (this._tableReadyPending && this._tableInstance) {
			this._tableReadyPending = false;
			this.props.onTableReady?.(this._tableInstance);
		}
	}

	private getVisibleItemById(items: T[], item?: T): T | null {
		if (!item) {
			return null;
		}

		return items.find((visibleItem) => visibleItem.id === item.id) ?? null;
	}

	private selectHandler = (items: T[]) => {
		this._pendingLocalSelectedIds = items.map((item) => item.id);
		return this.props.onSelect(items);
	};

	private focusHandler = (items: T[], isGoToItemNeeded: boolean = true) => {
		this._pendingLocalFocusedIds = items.map((item) => item.id);
		return this.props.onFocus(items, isGoToItemNeeded);
	};

	private focusRowHandler = (items: T[]) => this.focusHandler(items, false);

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	public override componentDidMount() {
		const {selected, focused, items} = this.props;
		const target = this.getVisibleItemById(items, focused.at(-1)) ?? this.getVisibleItemById(items, selected.at(-1));
		if (target) {
			this.table?.goToItem(target);
		}

		this._lastItemCount = items.length;
		window.addEventListener("viewsModifiedInLocalStorage", this.forceUpdateArrow);
		this.notifyTableReady();
	}

	public override UNSAFE_componentWillReceiveProps(nextProps: Readonly<IGridViewProps<T>>, nextContext: any): void {
		runInAction(() => {
			this._appState = nextProps.appState;
			this._feature = nextProps.feature;
			this._view = nextProps.view || this._appState.actions.getSelectedView(nextProps.feature);
		});
	}

	public override componentDidUpdate(prevProps: IGridViewProps<T>) {
		const {items, selected, focused, feature} = this.props;
		if (prevProps.appState !== this.props.appState && this._tableInstance) {
			this.bindTable(this._tableInstance);
		}
		const selectedIds = selected.map((item) => item.id);
		const focusedIds = focused.map((item) => item.id);
		const selectionChanged = !ArrayUtils.equals(this._committedSelectedIds, selectedIds);
		const focusChanged = !ArrayUtils.equals(this._committedFocusedIds, focusedIds);
		const localSelectionEcho = this._pendingLocalSelectedIds !== null && ArrayUtils.equals(this._pendingLocalSelectedIds, selectedIds);
		const localFocusEcho = this._pendingLocalFocusedIds !== null && ArrayUtils.equals(this._pendingLocalFocusedIds, focusedIds);
		if (selectionChanged || localSelectionEcho || feature !== prevProps.feature) {
			this._pendingLocalSelectedIds = null;
		}
		if (focusChanged || localFocusEcho || feature !== prevProps.feature) {
			this._pendingLocalFocusedIds = null;
		}
		this._committedSelectedIds = selectedIds;
		this._committedFocusedIds = focusedIds;
		this._lastItemCount = items.length;

		// Child props and refs are committed before this lifecycle. Match local
		// echoes by IDs so a different canvas selection can navigate immediately.
		const focusedItem = this.getVisibleItemById(items, focused.at(-1));
		const selectedItem = this.getVisibleItemById(items, selected.at(-1));
		if (focusChanged && !localFocusEcho && focusedItem) {
			this.table?.goToItem(focusedItem);
		} else if (selectionChanged && !localSelectionEcho && selectedItem) {
			this.table?.goToItem(selectedItem);
		} else if (feature !== prevProps.feature || items.length > prevProps.items.length) {
			const target = focusedItem ?? selectedItem;
			if (target) {
				this.table?.goToItem(target);
			}
		}
		this.notifyTableReady();
	}

	public override componentWillUnmount() {
		this.bindTable(null);
		window.removeEventListener("viewsModifiedInLocalStorage", this.forceUpdateArrow);
	}

	public override render() {
		const {
			items,
			checkboxColumn,
			selected,
			focused,
			onClick,
			onDoubleClick,
			feature,
			tableRowIcon,
			onAddClick,
			searchString,
			isSearching,
			appState,
			isCellContentsWrappingOn,
			prepareFieldsInRow,
			actionBarButtons,
			actionBarDefaultClick,
			onCloseGridActionBar,
			onDuplicateClick,
			emptyListText,
			selectedScreen,
			getItemColor,
			colorApplicationMode,
			getSpotlightFieldRefID,
			getColorApplicationMode,
			getSpotlightFieldColor,
			runReport,
		} = this.props;
		const {user, organizationIdByAuthData} = appState;
		const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
		const updatedValue = localStorage.getItem(localStorageKeyForWorkspaceViews);
		const viewIdsInLocalStorage: IViewTab[] = updatedValue ? JSON.parse(updatedValue) : [];
		const relevantFeatures = [XyiconFeature.Xyicon, XyiconFeature.Boundary];
		const isNoActiveView =
			relevantFeatures.includes(feature) &&
			!(feature !== XyiconFeature.SpaceEditor) &&
			viewIdsInLocalStorage.filter((v) => relevantFeatures.includes(appState.actions.getViewById(v.viewId)?.itemFeature)).length === 0;
		const view = this.getView();
		const data = appState.actions.sortItems(items, view);
		const searchedData = this.getSearchedItems(data);

		if (this.props.searchString.length > 0 && searchedData.length === 0) {
			return (
				<NoSearchResultsWrapper>
					<NoResultSearchViewV5 searchString={this.props.searchString} />
				</NoSearchResultsWrapper>
			);
		} else {
			return (
				<>
					{(items.length > 0 || isSearching) && !isNoActiveView ? (
						<TableV5
							ref={this.bindTable}
							getHeaders={this.getHeaders}
							onHeaderWidthChange={this.onHeaderWidthChange}
							data={data}
							searchedData={searchedData}
							getKey={getModelId}
							getFields={this.getFields}
							getFieldsArray={this.getFieldsArray}
							selected={selected}
							focused={focused}
							onSelect={this.selectHandler}
							onFocus={this.focusRowHandler}
							onClick={onClick}
							onDoubleClick={onDoubleClick}
							onReorderColumn={this.onReorderColumn}
							checkboxColumn={checkboxColumn}
							sorts={view.sorts}
							view={view}
							onToggleSort={this.onToggleSort}
							moduleName={appState.selectedModuleTitle}
							feature={feature}
							tableRowIcon={tableRowIcon}
							searchString={searchString}
							isSearching={isSearching}
							isCellContentsWrappingOn={isCellContentsWrappingOn}
							prepareFieldsInRow={prepareFieldsInRow}
							onManageColumns={this.props.onManageColumns && this.onManageColumns}
							onTableRowMouseMove={this.onTableRowMouseMove}
							onDuplicateClick={onDuplicateClick}
							getItemColor={getItemColor}
							colorApplicationMode={colorApplicationMode}
							getSpotlightFieldRefID={getSpotlightFieldRefID}
							getColorApplicationMode={getColorApplicationMode}
							getSpotlightFieldColor={getSpotlightFieldColor}
							runReport={runReport}
						/>
					) : isNoActiveView ? (
						<EmptyListViewV5
							onAddClick={onAddClick}
							feature={XyiconFeature.View}
						/>
					) : (
						<EmptyListViewV5
							onAddClick={onAddClick}
							feature={feature}
							text={emptyListText}
						/>
					)}
					{selected.length > 0 && actionBarButtons?.length > 0 && (
						<GridActionBarV5
							items={selected}
							feature={feature}
							buttons={actionBarButtons}
							defaultClick={actionBarDefaultClick}
							onClose={onCloseGridActionBar}
							onFocus={this.focusHandler}
							onSelect={this.selectHandler}
							selectedScreen={selectedScreen}
						/>
					)}
				</>
			);
		}
	}
}
